import React, { useState, useEffect } from 'react';
import { getUserProfile, updatePassword } from '../services/api';

const Profil = ({ user: initialUser }) => {
  const [user, setUser] = useState(initialUser);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    if (initialUser) {
      setUser(initialUser);
    } else {
      loadUserProfile();
    }
  }, [initialUser]);

  const loadUserProfile = async () => {
    setLoading(true);
    try {
      const profile = await getUserProfile();
      setUser(profile);
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Erreur lors du chargement du profil',
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({
        type: 'error',
        text: 'Les mots de passe ne correspondent pas',
      });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({
        type: 'error',
        text: 'Le mot de passe doit contenir au moins 6 caractères',
      });
      return;
    }

    setLoading(true);
    try {
      const result = await updatePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      );
      setMessage({
        type: 'success',
        text: result.message || 'Mot de passe modifié avec succès',
      });
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setShowPasswordForm(false);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erreur lors de la modification du mot de passe',
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading && !user) {
    return (
      <div className="p-8 bg-background min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
          <p className="text-text-light">Chargement du profil...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-8 bg-background min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Impossible de charger le profil</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-background min-h-screen">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-text mb-8 flex items-center">
          <i className="fas fa-user-circle mr-3 text-primary"></i>
          Mon Profil
        </h1>

        {/* Informations du profil */}
        <div className="bg-cardBg rounded-lg shadow-lg p-8 mb-6">
          <div className="flex items-center mb-6">
            <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center text-white text-3xl font-bold mr-6">
              {user.nom?.[0]?.toUpperCase() || user.username?.[0]?.toUpperCase() || 'U'}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-text">
                {user.nom} {user.prenom}
              </h2>
              <p className="text-text-light">@{user.username}</p>
              <span
                className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-medium ${
                  user.role === 'admin'
                    ? 'bg-purple-100 text-purple-800'
                    : user.role === 'almer'
                    ? 'bg-orange-100 text-orange-800'
                    : user.role === 'ministre'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-blue-100 text-blue-800'
                }`}
              >
                {user.role === 'admin'
                  ? 'Administrateur'
                  : user.role === 'almer'
                  ? 'ALMER'
                  : user.role === 'ministre'
                  ? 'Ministre'
                  : 'Utilisateur'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-text-light mb-1">
                Nom d'utilisateur
              </label>
              <p className="text-text font-medium">{user.username}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-light mb-1">Email</label>
              <p className="text-text font-medium">{user.email}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-light mb-1">Nom</label>
              <p className="text-text font-medium">{user.nom || '-'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-light mb-1">Prénom</label>
              <p className="text-text font-medium">{user.prenom || '-'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-light mb-1">Téléphone</label>
              <p className="text-text font-medium">{user.telephone || '-'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-light mb-1">Statut</label>
              <span
                className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                  user.statut === 'actif'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {user.statut === 'actif' ? 'Actif' : 'Inactif'}
              </span>
            </div>
          </div>
        </div>

        {/* Modification du mot de passe */}
        <div className="bg-cardBg rounded-lg shadow-lg p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-text flex items-center">
              <i className="fas fa-lock mr-2 text-primary"></i>
              Modifier le mot de passe
            </h2>
            <button
              onClick={() => {
                setShowPasswordForm(!showPasswordForm);
                setMessage({ type: '', text: '' });
                setPasswordData({
                  currentPassword: '',
                  newPassword: '',
                  confirmPassword: '',
                });
              }}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all"
            >
              {showPasswordForm ? (
                <>
                  <i className="fas fa-times mr-2"></i>Annuler
                </>
              ) : (
                <>
                  <i className="fas fa-edit mr-2"></i>Modifier
                </>
              )}
            </button>
          </div>

          {showPasswordForm && (
            <form onSubmit={handlePasswordSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Mot de passe actuel *
                  </label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    placeholder="Entrez votre mot de passe actuel"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Nouveau mot de passe *
                  </label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    required
                    minLength={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    placeholder="Entrez votre nouveau mot de passe (min. 6 caractères)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Confirmer le nouveau mot de passe *
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                    minLength={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    placeholder="Confirmez votre nouveau mot de passe"
                  />
                </div>
              </div>

              {message.text && (
                <div
                  className={`mt-4 p-4 rounded-lg ${
                    message.type === 'success'
                      ? 'bg-green-100 text-green-700 border border-green-400'
                      : 'bg-red-100 text-red-700 border border-red-400'
                  }`}
                >
                  <i
                    className={`fas ${
                      message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'
                    } mr-2`}
                  ></i>
                  {message.text}
                </div>
              )}

              <div className="mt-6 flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>Modification...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save mr-2"></i>Enregistrer
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profil;

